/**
 * ==========================================================================
 *  THE KLAPI IN THE OTHER HAND
 * ==========================================================================
 *  Between the klapikasa and the kamiina's hatch, Kimmo is running with one
 *  split log in his left arm. This is that log.
 *
 *  It exists for one reason: the rack is on the tub's north rim and the hatch
 *  is off its east end, so the two presses of F are a corner apart, and in
 *  between them the *only* thing that tells you the first one landed is what
 *  is in your hands. A HUD prompt does not do that job - the prompt is gone
 *  the moment you are past the rack, and the question you are asking at the
 *  tub's corner is "did I actually get it?".
 *
 *  It is asked twice a run, because the pesä wants a pari and they go in one
 *  at a time. On the second lap the same log in the same hand is the answer to
 *  a different question - "am I carrying, or am I still going back for it?" -
 *  which is why an empty hand has to read as emphatically empty.
 *
 *  Same three tricks as the bottle it is carried opposite (see
 *  `BeerViewmodel`): parented to the camera, drawn with depth testing off and
 *  last, and left out of the fog. It is a box rather than a photograph because
 *  a klapi *is* a box - one split billet, bark on two faces - and because the
 *  bottle's joke is that it is a real photograph and doing it twice would
 *  spend the joke.
 *
 *  Purely cosmetic. `Kamiina` in src/world/ decides whether there is wood in
 *  hand, and nothing here is ever read back.
 * ==========================================================================
 */

import * as THREE from 'three';
import { KLAPI_VIEWMODEL } from '../config/viewmodel';

export interface KlapiFrame {
  /** Render seconds since the last frame. */
  dt: number;
  /** Whether there is wood in Kimmo's hand at all. */
  holding: boolean;
  /** Horizontal ground speed, m/s. Drives the bob. */
  speed: number;
  /** The camera's head bob this frame, in metres. */
  bobLateral: number;
  bobVertical: number;
  swayIntensity: number;
}

export class KlapiViewmodel {
  private readonly pivot = new THREE.Group();
  private readonly geometry = new THREE.BoxGeometry(1, 1, 1);
  private readonly materials: THREE.MeshBasicMaterial[];
  /** 0 while empty-handed, 1 while carrying. Everything else is derived. */
  private shown = 0;
  private bob = 0;

  constructor(private readonly camera: THREE.PerspectiveCamera) {
    // Sawn ends light, bark dark, and the split faces between the two. Six
    // materials on a box is the cheapest way to get a billet that does not
    // read as a brick.
    this.materials = [
      KLAPI_VIEWMODEL.bark,
      KLAPI_VIEWMODEL.bark,
      KLAPI_VIEWMODEL.split,
      KLAPI_VIEWMODEL.split,
      KLAPI_VIEWMODEL.sawn,
      KLAPI_VIEWMODEL.sawn,
    ].map(
      (color) =>
        new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          depthTest: false,
          depthWrite: false,
          fog: false,
        }),
    );

    const mesh = new THREE.Mesh(this.geometry, this.materials);
    mesh.renderOrder = 999;
    mesh.frustumCulled = false;
    mesh.scale.set(KLAPI_VIEWMODEL.length, KLAPI_VIEWMODEL.thickness, KLAPI_VIEWMODEL.width);
    this.pivot.add(mesh);
    this.pivot.visible = false;
    camera.add(this.pivot);
  }

  reset(): void {
    this.shown = 0;
    this.bob = 0;
    this.pivot.visible = false;
  }

  update(frame: KlapiFrame): void {
    // Picked up and put down over a couple of frames rather than switched, so
    // the log arrives in the hand instead of appearing in it.
    const target = frame.holding ? 1 : 0;
    const rate = frame.dt / KLAPI_VIEWMODEL.raiseDuration;
    this.shown += Math.max(-rate, Math.min(rate, target - this.shown));

    this.pivot.visible = this.shown > 0.002;
    if (!this.pivot.visible) return;

    const halfHeight =
      Math.tan(THREE.MathUtils.degToRad(this.camera.fov) / 2) * KLAPI_VIEWMODEL.depth;
    const halfWidth = halfHeight * this.camera.aspect;

    // The run, felt through an armful of wood: a slow figure-of-eight rather
    // than the bottle's swing, because a log is heavy and does not swing.
    this.bob += frame.dt * Math.min(frame.speed, KLAPI_VIEWMODEL.bobSpeedCap) * KLAPI_VIEWMODEL.bobRate;
    const sway = frame.swayIntensity;
    const x = KLAPI_VIEWMODEL.x + Math.sin(this.bob) * KLAPI_VIEWMODEL.bobAmount + frame.bobLateral * sway;
    const y =
      KLAPI_VIEWMODEL.y -
      (1 - this.shown) * KLAPI_VIEWMODEL.raiseFrom +
      Math.sin(this.bob * 2) * KLAPI_VIEWMODEL.bobAmount * 0.6 +
      frame.bobVertical * sway;

    this.pivot.position.set(x * halfWidth, y * halfHeight, -KLAPI_VIEWMODEL.depth);
    this.pivot.rotation.set(
      KLAPI_VIEWMODEL.pitch,
      KLAPI_VIEWMODEL.yaw,
      KLAPI_VIEWMODEL.roll + Math.sin(this.bob) * 0.06,
    );
    for (const material of this.materials) material.opacity = this.shown;
  }

  dispose(): void {
    this.camera.remove(this.pivot);
    this.geometry.dispose();
    for (const material of this.materials) material.dispose();
  }
}
